import { type ReactNode, type Ref, forwardRef, useMemo } from 'react';
import {
  KeyboardAwareScrollView,
  type KeyboardAwareScrollViewProps,
} from 'react-native-keyboard-controller';
import Reanimated, { type AnimatedRef } from 'react-native-reanimated';

import { Bounded } from './Bounded';

export interface BoundedKeyboardAwareScrollViewProps extends Omit<
  KeyboardAwareScrollViewProps,
  'ScrollViewComponent'
> {
  children?: ReactNode;
  /**
   * Classes for the inner bounded content wrapper — padding/gap the way
   * `BoundedScrollView`'s `contentContainerClassName` works. `KeyboardAwareScrollView`
   * is a non-react-native component, so Uniwind drops `contentContainerClassName`;
   * we apply it to a `<Bounded>` child instead (same approach as `AuthScreen`).
   */
  contentClassName?: string;
  /**
   * Animated ref to the underlying scroll node, for consumers that need it (e.g.
   * `react-native-sortables`' `scrollableRef` for drag auto-scroll). KAS keeps its
   * own internal animated ref; when this is provided we hand KAS a capturing
   * `ScrollViewComponent` that also publishes the real node here.
   */
  scrollRef?: AnimatedRef<Reanimated.ScrollView>;
}

/**
 * Keyboard-aware sibling of `BoundedScrollView`: `react-native-keyboard-controller`'s
 * `KeyboardAwareScrollView` (scroll-into-view above the software keyboard) with the
 * global page-width cap + content padding applied via an inner `<Bounded>`. Use on full
 * screens whose inputs would otherwise sit behind the keyboard (see `AuthScreen`).
 */
export function BoundedKeyboardAwareScrollView({
  children,
  contentClassName,
  scrollRef,
  keyboardShouldPersistTaps = 'handled',
  ...props
}: BoundedKeyboardAwareScrollViewProps) {
  // When a caller needs the scroll node as an animated ref (sortable), wrap
  // Reanimated.ScrollView so KAS's own ref and the caller's ref both attach to the
  // real node. This mirrors KAS's internal `useCombinedRef` (a callback ref that
  // calls function refs and sets object refs).
  const ScrollViewComponent = useMemo(() => {
    if (!scrollRef) return undefined;
    // Private adapter, not a screen component — KAS requires `ScrollViewComponent`
    // to be a component, so this can't be hoisted out as a plain helper.
    // eslint-disable-next-line react/no-multi-comp
    const Capturing = forwardRef<Reanimated.ScrollView>((innerProps, kasRef) => {
      const setRef = (node: Reanimated.ScrollView | null) => {
        for (const r of [kasRef, scrollRef] as Ref<Reanimated.ScrollView>[]) {
          if (!r) continue;
          if (typeof r === 'function') r(node);
          else (r as { current: Reanimated.ScrollView | null }).current = node;
        }
      };
      return <Reanimated.ScrollView ref={setRef} {...innerProps} />;
    });
    Capturing.displayName = 'CapturingScrollView';
    return Capturing;
  }, [scrollRef]);

  return (
    <KeyboardAwareScrollView
      bottomOffset={100}
      keyboardShouldPersistTaps={keyboardShouldPersistTaps}
      style={{ flex: 1 }}
      contentContainerStyle={{ flexGrow: 1 }}
      ScrollViewComponent={ScrollViewComponent}
      {...props}
    >
      <Bounded className={contentClassName}>{children}</Bounded>
    </KeyboardAwareScrollView>
  );
}
